import type { ComponentAssociation } from "building-types/components/componentAssociationTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityHandle, OptionSetHandle } from "building-types/handles/docEntityHandleTypes";
import {
  isComponentAssociatedEntityHandle,
  isComponentOwnedMemberEntityHandle,
} from "building-types/handles/docEntityHandleTypes";
import { deriveInstanceOptionSetId } from "building-types/options/instanceOptionSetId";
import type { InstanceOptionSet } from "building-types/options/optionSet";
import { deleteIn, setIn } from "common/utils/objectUtils";
import { docAssociationTableConfigs } from "./docAssociationTableTools";
import { docGetComponentInstances, docGetComponentRecursiveParentComponents } from "./docComponentInstances";
import { docEntityHandleHash } from "./entity/core/handleHash";
import { docEntityGetComponent } from "./entity/docGetEntityComponent";
import { componentAssociationNew, docGetComponentAssociation } from "./options/componentAssociations";

export type DocRefMembershipOp = "add" | "remove" | "update";

export interface DocRefMembershipScope {
  /** Container entity is present in the new entity-ref source set. */
  readonly containerIncluded: boolean;
  /** Member entity lists this container in its includedBy roots. */
  readonly memberIncludedByContainer: boolean;
  /**
   * mirror: add only when srcDoc has the membership (doc-ref sync).
   * ensure: add when local membership is missing (manual component membership).
   */
  readonly srcPolicy?: "mirror" | "ensure";
}

export type DocMutationPath = [keyof Doc, ...(string | number)[]];

export interface DocRefMembershipMutations {
  readonly deletions: DocMutationPath[];
  readonly sets: [DocMutationPath, unknown][];
}

function forEachAffectedComponentInstance(
  doc: Doc,
  componentId: string,
  visit: (componentInstance: { readonly id: string; readonly name: string }) => void,
): void {
  const affectedComponentIds = [componentId, ...docGetComponentRecursiveParentComponents(doc, componentId)];

  for (const affectedComponentId of affectedComponentIds) {
    const componentInstances = docGetComponentInstances(doc, { type: "COMPONENT", id: affectedComponentId });
    for (const componentInstance of componentInstances) {
      visit(componentInstance);
    }
  }
}

function appendInstanceOptionSetMutations(
  doc: Doc,
  optionSetHandle: OptionSetHandle,
  componentId: string,
  sets: [DocMutationPath, unknown][],
): void {
  forEachAffectedComponentInstance(doc, componentId, (componentInstance) => {
    const instanceOptionSetId = deriveInstanceOptionSetId(optionSetHandle.id, componentInstance.id);
    if (!doc.instanceOptionSets[instanceOptionSetId]) {
      const instanceOptionSet: InstanceOptionSet = {
        id: instanceOptionSetId,
        name: `${componentInstance.name} Option Set`,
        entityType: "INSTANCE_OPTION_SET",
        archetypeId: optionSetHandle.id,
        componentInstanceId: componentInstance.id,
        disabled: false,
        sortIndex: 0,
      };
      sets.push([["instanceOptionSets", instanceOptionSetId], instanceOptionSet]);
    }

    const archetypeOptionSet = doc.optionSets[optionSetHandle.id];
    const archetypeChoice = doc.optionChoices?.[optionSetHandle.id] ?? archetypeOptionSet?.defaultValue ?? null;
    if (archetypeChoice !== null && doc.optionChoices?.[instanceOptionSetId] === undefined) {
      sets.push([["optionChoices", instanceOptionSetId], archetypeChoice]);
    }
  });
}

function appendInstanceOptionSetDeletions(
  doc: Doc,
  optionSetHandle: OptionSetHandle,
  componentId: string,
  deletions: DocMutationPath[],
): void {
  forEachAffectedComponentInstance(doc, componentId, (componentInstance) => {
    const instanceOptionSetId = deriveInstanceOptionSetId(optionSetHandle.id, componentInstance.id);
    if (doc.instanceOptionSets[instanceOptionSetId]) {
      deletions.push(["instanceOptionSets", instanceOptionSetId]);
    }
  });
}

export function computeDocRefMembershipMutations(
  doc: Doc,
  srcDoc: Doc | undefined,
  memberHandle: DocEntityHandle,
  containerId: string,
  op: DocRefMembershipOp,
  scope: DocRefMembershipScope,
): DocRefMembershipMutations {
  const deletions: DocMutationPath[] = [];
  const sets: [DocMutationPath, unknown][] = [];

  if (isComponentAssociatedEntityHandle(memberHandle)) {
    const localAssociation = docGetComponentAssociation(doc, memberHandle, containerId);

    let srcAssociation: ComponentAssociation | undefined;
    let shouldHave = false;
    if (op !== "remove" && scope.memberIncludedByContainer && scope.containerIncluded) {
      if (scope.srcPolicy === "ensure") {
        shouldHave = true;
      } else if (srcDoc) {
        srcAssociation = docGetComponentAssociation(srcDoc, memberHandle, containerId);
        shouldHave = srcAssociation !== undefined;
      }
    }

    if (!shouldHave) {
      if (localAssociation) {
        deletions.push(["componentAssociations", localAssociation.id]);
        appendInstanceOptionSetDeletions(doc, memberHandle, containerId, deletions);
      }
    } else {
      if (!localAssociation) {
        // Mirror already resolved srcAssociation when probing membership above.
        // Ensure skips that probe (membership is unconditional) and only fetches src
        // here to copy sortIndex when creating a new row.
        if (srcAssociation === undefined && srcDoc) {
          srcAssociation = docGetComponentAssociation(srcDoc, memberHandle, containerId);
        }
        const association = componentAssociationNew(memberHandle, containerId, srcAssociation?.sortIndex ?? 0);
        sets.push([["componentAssociations", association.id], association]);
      }
      // Instance option sets are created when the association is first added and when
      // component instances are placed. Skip the instance walk on mirror updates where
      // the association already exists. Ensure (manual membership) still re-checks.
      if (!localAssociation || scope.srcPolicy === "ensure") {
        appendInstanceOptionSetMutations(doc, memberHandle, containerId, sets);
      }
    }

    return { deletions, sets };
  }

  if (isComponentOwnedMemberEntityHandle(memberHandle)) {
    if (op === "remove") {
      return { deletions, sets };
    }

    const srcComponent = srcDoc ? docEntityGetComponent(srcDoc, memberHandle) : undefined;
    if (srcComponent?.id === containerId) {
      sets.push([["componentMembers", docEntityHandleHash(memberHandle)], containerId]);
    }
  }

  return { deletions, sets };
}

export function applyDocRefMembershipMutations(doc: Doc, mutations: DocRefMembershipMutations): Doc {
  let result = doc;
  for (const [path, value] of mutations.sets) {
    result = setIn(result, path, value);
  }
  for (const path of mutations.deletions) {
    result = deleteIn(result, path);
  }
  return result;
}

/**
 * Sync local component membership for one member under one container during doc-ref import/update/remove.
 */
export function docSyncRefMembership(
  doc: Doc,
  srcDoc: Doc | undefined,
  memberHandle: DocEntityHandle,
  containerId: string,
  op: DocRefMembershipOp = "update",
  scope: DocRefMembershipScope = { containerIncluded: true, memberIncludedByContainer: true },
): Doc {
  const mutations = computeDocRefMembershipMutations(doc, srcDoc, memberHandle, containerId, op, scope);
  return applyDocRefMembershipMutations(doc, mutations);
}

function includedByComponentIds(sourceIncludedBy: { readonly [hash: string]: DocEntityHandle }): Set<string> {
  const componentIds = new Set<string>();
  for (const hash in sourceIncludedBy) {
    const handle = sourceIncludedBy[hash];
    if (handle.type === "COMPONENT") {
      componentIds.add(handle.id);
    }
  }
  return componentIds;
}

function containerIdsForRefMembershipSync(
  doc: Doc,
  memberHandle: DocEntityHandle,
  includedByComponents: ReadonlySet<string>,
  op: DocRefMembershipOp,
): Set<string> {
  const containerIds = new Set<string>(includedByComponents);

  if (op === "update" && isComponentAssociatedEntityHandle(memberHandle)) {
    for (const config of docAssociationTableConfigs) {
      if (!config.isMemberHandle(memberHandle)) {
        continue;
      }
      for (const localAssociation of config.listForMember(doc, memberHandle)) {
        const containerId = config.getContainerId(localAssociation);
        // Skip containers outside the member's doc ref, like local components.
        if (!config.isContainerOwnedByMemberDocRef(doc, memberHandle, containerId)) {
          continue;
        }
        containerIds.add(containerId);
      }
    }
  }

  return containerIds;
}

/**
 * Apply doc-ref membership sync for all containers referenced by an entity-ref source entry.
 */
export function docApplyRefMembershipFromSource(
  doc: Doc,
  srcDoc: Doc | undefined,
  memberHandle: DocEntityHandle,
  sourceIncludedBy: { readonly [hash: string]: DocEntityHandle },
  op: DocRefMembershipOp,
  containerIncluded: Record<string, true>,
  batch: { deletionPaths: (string | number)[][]; setInPaths: [(string | number)[], unknown][] },
): void {
  if (!isComponentAssociatedEntityHandle(memberHandle)) {
    return;
  }

  const includedByComponents = includedByComponentIds(sourceIncludedBy);
  // add/remove only sync includedBy containers; update also merges local association containers.
  const containerIds =
    op === "update"
      ? containerIdsForRefMembershipSync(doc, memberHandle, includedByComponents, op)
      : includedByComponents;

  for (const containerId of containerIds) {
    if (op === "add" && !containerIncluded[containerId]) {
      continue;
    }

    const mutations = computeDocRefMembershipMutations(doc, srcDoc, memberHandle, containerId, op, {
      containerIncluded: op === "remove" || !!containerIncluded[containerId],
      memberIncludedByContainer: includedByComponents.has(containerId),
    });

    for (const path of mutations.deletions) {
      batch.deletionPaths.push(path);
    }
    for (const [path, value] of mutations.sets) {
      batch.setInPaths.push([path, value]);
    }
  }
}
